import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { ErrorMessage, type FormikErrors, FormikTouched } from "formik";
import NiceModal from "@ebay/nice-modal-react";
import { StockStrategy, type Config, type Prize } from "kadow-jetpack-model";
import { faStar, faTrash } from "@fortawesome/free-solid-svg-icons";
import KC_Heading3 from "../../../../designsystem/typography/KC-Heading/KC_Heading3";
import KC_UIText from "../../../../designsystem/typography/KC_UIText/KC_UIText";
import KC_Input from "../../../../designsystem/KC_FormInput/KC_Input";
import KC_UISmall from "../../../../designsystem/typography/KC_UITextSmall/KC_UISmall";
import KC_Toggle from "../../../../designsystem/KC_FormInput/KC_Toggle";
import { KC_Weights } from "../../../../designsystem/ds_commons";
import EmojiPickerWrapperJP from "../../../../components/GiftsEditor/components/EmojiPickerWrapperJP";
import TSUtils from "../../../../utils/helpers/TSUtils";
import { tt } from "../../../../utils/i18n/i18n";
import KC_Icon_Button from "../../../../designsystem/KC_Button/KC_Icon_Button";
import KC_Button, {
  KC_ButtonSize,
  KC_ButtonType
} from "../../../../designsystem/KC_Button/KC_Button";
import TrashCanIcon from "../../../../components/Icons/TrashCanIcon";
import GiftSuggestionModal from "../../../../components/modal/Onboarding/GiftSuggestion/GiftSuggestionModal";
import ExpiryDelaySelector from "../../../19_JetpackConfig/components/GiftsConfiguration/component/ExpiryDelaySelector";

interface GiftFormProps {
  prizeIndex: number;
  config: Config;
  hasAdvancedCouponOptions: boolean;
  hideGenerateIdea?: boolean;
  hideRemove?: boolean;
  setFieldValue: (field: string, value: any) => void;
  setFieldTouched?: (field: string, value: any) => void;
  removeItem?: () => void;
  addItem?: <X = any>(index: number, value: X) => void;
  errors: FormikErrors<Config>;
  touched?: FormikTouched<Config>;
  onClickGenerateIdea?: () => void;
}

export const max_characters_count = 50;

export default function GiftForm(props: GiftFormProps) {
  const [showConditionsInputs, setShowConditionsInputs] = React.useState(false);
  const quantityInputFocused = useRef(false);

  const quantityInnerRef = useCallback((el: HTMLInputElement | null) => {
    if (el && !quantityInputFocused.current) {
      quantityInputFocused.current = true;
      el.focus();
    }
  }, []);

  const handleFieldTouched = (name: string) => {
    if (props.setFieldTouched) {
      props.setFieldTouched(name, true);
    }
  };

  function renderPercentageField() {
    return (
      <div className="w-100 d-flex flex-column">
        <KC_Input
          id={`prizes_${props.prizeIndex}_pc`}
          placeholder={tt("gift_percentage_placeholder")}
          htmlFor={`prizes[${props.prizeIndex}].pc`}
          setFieldValue={props.setFieldValue}
          onBlur={() => handleFieldTouched(`prizes[${props.prizeIndex}].pc`)}
          type={"number"}
          min={0}
          step={0.1}
          max={100}
          rightIcon={"%"}
          required={true}
          value={
            props.config.prizes[props.prizeIndex] &&
            TSUtils.isNumber(props.config.prizes[props.prizeIndex].pc)
              ? props.config.prizes[props.prizeIndex].pc
              : ""
          }
        />
      </div>
    );
  }

  function renderUnBlurDelay() {
    return (
      <select
        className="form-control kc-input-wrapper"
        value={props.config.prizes[props.prizeIndex].unBlurDelay}
        onChange={(e) => {
          const val = parseInt(e.target.value);
          if (isNaN(val)) {
            return;
          }
          props.setFieldValue(`prizes[${props.prizeIndex}].unBlurDelay`, val);
        }}
        required={true}
      >
        <option value={1}>{tt("coupon_immediate_placeholder")}</option>
        <option value={-1}>{tt("coupon_next_day_placeholder")}</option>
      </select>
    );
  }

  function renderQuantityField() {
    return (
      <>
        <KC_Input
          id={`prizes_${props.prizeIndex}_iQty`}
          htmlFor={`prizes[${props.prizeIndex}].iQty`}
          setFieldValue={props.setFieldValue}
          onBlur={() => handleFieldTouched(`prizes[${props.prizeIndex}].iQty`)}
          innerRef={quantityInnerRef}
          type={"number"}
          placeholder={tt("quantity_per_week_placeholder")}
          min={1}
          required={true}
          value={
            props.config.prizes[props.prizeIndex] &&
            TSUtils.isNumber(props.config.prizes[props.prizeIndex].iQty)
              ? (props.config.prizes[props.prizeIndex].iQty as number)
              : 0
          }
        />
      </>
    );
  }

  function renderPrizeCondition() {
    return (
      <div id="condition-toggle">
        <div className={"w-100 d-flex align-items-center gap-md"}>
          <KC_Toggle
            id={`condition-${props.prizeIndex}`}
            isChecked={showConditionsInputs}
            onToggle={() => {
              toggleShowConditions(!showConditionsInputs);
            }}
          />
          <KC_UISmall
            className="h-100 d-flex align-items-center"
            weight={KC_Weights.reg}
            text={tt("jp_onboarding_step_gift_toggle_label_condition")}
          />
        </div>
        {showConditionsInputs && (
          <div className="d-flex flex-column st-gap mt-4">
            <label>
              <KC_UIText
                weight={KC_Weights.bold}
                text={tt("jp_onboarding_step_gift_label_condition")}
              />
            </label>
            <div className="w-100 d-flex flex-column">
              <KC_Input
                id={`prizes_${props.prizeIndex}_conditions_0`}
                htmlFor={`prizes[${props.prizeIndex}].conditions[0]`}
                setFieldValue={props.setFieldValue}
                onBlur={() =>
                  handleFieldTouched(
                    `prizes[${props.prizeIndex}].conditions[0]`
                  )
                }
                value={props.config.prizes?.[props.prizeIndex]?.conditions?.[0]}
                placeholder={tt(
                  "jp_onboarding_step_gift_placeholder_condition_01"
                )}
                required={showConditionsInputs}
              />
            </div>
            <div className="w-100 d-flex flex-column">
              <KC_Input
                id={`prizes_${props.prizeIndex}_conditions_1`}
                htmlFor={`prizes[${props.prizeIndex}].conditions[1]`}
                setFieldValue={props.setFieldValue}
                onBlur={() =>
                  handleFieldTouched(
                    `prizes[${props.prizeIndex}].conditions[1]`
                  )
                }
                value={props.config.prizes?.[props.prizeIndex]?.conditions?.[1]}
                placeholder={tt(
                  "jp_onboarding_step_gift_placeholder_condition_02"
                )}
                required={false}
              />
            </div>
            <div className="w-100 d-flex flex-column">
              <KC_Input
                id={`prizes_${props.prizeIndex}_conditions_2`}
                htmlFor={`prizes[${props.prizeIndex}].conditions[2]`}
                setFieldValue={props.setFieldValue}
                onBlur={() =>
                  handleFieldTouched(
                    `prizes[${props.prizeIndex}].conditions[2]`
                  )
                }
                value={props.config.prizes?.[props.prizeIndex]?.conditions?.[2]}
                placeholder={tt(
                  "jp_onboarding_step_gift_placeholder_condition_03"
                )}
                required={false}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  function toggleShowConditions(newValue: boolean) {
    setShowConditionsInputs(newValue);

    const newPrize = { ...prize };

    if (newValue) {
      if (newPrize.conditions?.length > 0) {
        return;
      }
      newPrize.conditions = [tt("default_redeem_condition")];
    } else {
      newPrize.conditions = [];
    }

    props.setFieldValue(`prizes.${props.prizeIndex}`, newPrize);
  }

  const prize = useMemo(() => {
    return props.config?.prizes?.[props.prizeIndex];
  }, [props.config]);

  const isShowRemove = useMemo(() => {
    return !props.hideRemove;
  }, [props.hideRemove]);

  const emojiValue = useMemo(() => {
    return (
      !!props.config &&
      !!props.config.prizes &&
      !!props.config.prizes[props.prizeIndex] &&
      props.config.prizes[props.prizeIndex].image
    );
  }, [props.config, props.prizeIndex]);

  const prizeName = useMemo(() => {
    const name = props.config.prizes?.[props.prizeIndex]?.name;
    return (
      name ||
      `${tt("jp_onboarding_step_gift_gifts_text")} ${props.prizeIndex + 1}`
    );
  }, [props.prizeIndex, props.config]);

  useEffect(() => {
    if (prize.conditions?.length > 0) {
      setShowConditionsInputs(true);
    }
  }, [prize]);

  return (
    <div
      id={`gift-form-${props.prizeIndex}`}
      className="w-100 d-flex flex-column st-gap position-relative"
    >
      <div className="w-auto mx-auto">
        <KC_Heading3 text={prizeName} />
      </div>
      {isShowRemove && (
        <KC_Icon_Button
          icon={faTrash}
          size={KC_ButtonSize.Large}
          isInverted={true}
          onClick={props.removeItem}
          className="position-absolute"
          extraStyle={{
            top: -16,
            right: -16
          }}
          customIcon={<TrashCanIcon />}
        />
      )}
      <div className="w-100 d-flex flex-column align-items-center">
        <div
          className="w-100 h-100 d-flex justify-content-center align-items-center"
          style={{
            width: 68,
            borderRadius: 6
          }}
        >
          <div>
            <EmojiPickerWrapperJP
              initialEmoji={emojiValue}
              onEmojiSelect={(emoji) => {
                props.setFieldValue(`prizes[${props.prizeIndex}].image`, emoji);
                handleFieldTouched(`prizes[${props.prizeIndex}].image`);
              }}
            />
          </div>
        </div>
        <ErrorMessage
          name={`prizes[${props.prizeIndex}].image`}
          className="error"
          component="div"
        />
      </div>
      <div className="d-flex st-gap">
        <div className="h-100 flex-grow-1">
          <label htmlFor={`prizes[${props.prizeIndex}].name`} className="m-0">
            <KC_UIText
              weight={KC_Weights.bold}
              text={tt("jp_onboarding_step_gift_gift_name")}
            />
          </label>
          <div className="mb-2">
            <KC_UIText
              weight={"reg"}
              text={tt("jp_onboarding_giftform_helper_format")}
            />
          </div>
          <KC_Input
            id={`prizes_${props.prizeIndex}_name`}
            htmlFor={`prizes[${props.prizeIndex}].name`}
            onBlur={() =>
              handleFieldTouched(`prizes[${props.prizeIndex}].name`)
            }
            setFieldValue={props.setFieldValue}
            required={true}
            placeholder={tt("jp_onboarding_giftform_placeholder_name")}
            value={props.config.prizes?.[props.prizeIndex]?.name}
          />
        </div>
      </div>
      {!props.hideGenerateIdea && (
        <div className="w-100 d-flex justify-content-end">
          <KC_Button
            isInverted={true}
            showIconOnTheLeft={true}
            icon={faStar}
            size={KC_ButtonSize.Small}
            text={tt("jp_onboarding_giftform_generate_ideas")}
            type={KC_ButtonType.Button}
            onClick={async () => {
              props.onClickGenerateIdea?.();

              const newPrize: Partial<Prize> =
                await NiceModal.show(GiftSuggestionModal);

              if (newPrize) {
                props.setFieldValue(
                  `prizes[${props.prizeIndex}].name`,
                  newPrize.name
                );
                props.setFieldValue(
                  `prizes[${props.prizeIndex}].image`,
                  newPrize.image
                );
              }
            }}
            className="w-25 border display-lg d-flex"
          />
          <KC_Button
            isInverted={true}
            showIconOnTheLeft={true}
            icon={faStar}
            size={KC_ButtonSize.Giant}
            text={tt("jp_onboarding_giftform_generate_ideas")}
            type={KC_ButtonType.Button}
            onClick={async () => {
              props.onClickGenerateIdea?.();

              const newPrize: Partial<Prize> =
                await NiceModal.show(GiftSuggestionModal);
              if (newPrize) {
                props.setFieldValue(
                  `prizes[${props.prizeIndex}].name`,
                  newPrize.name
                );
              }
            }}
            className="w-100 border display-sm d-flex"
          />
        </div>
      )}
      <div className={"w-100 d-inline-block st-gap align-items-end"}>
        <label htmlFor={`prizes[${props.prizeIndex}].pc`} className="m-0">
          <KC_UIText
            weight={KC_Weights.bold}
            text={tt("jp_onboarding_giftform_label_percentage")}
          />
        </label>
      </div>
      <div className={"w-100 d-flex st-gap align-items-end"}>
        <div className="d-flex flex-row flex-wrap gap-sm">
          {renderPercentageField()}
          <div className="d-flex align-items-center gap-sm">
            <KC_Toggle
              id={`prize-strategy-${props.prizeIndex}`}
              isChecked={
                props.config.prizes[props.prizeIndex].ss === StockStrategy.Week
              }
              onToggle={() => {
                const isWeek =
                  props.config.prizes[props.prizeIndex].ss ===
                  StockStrategy.Week;
                props.setFieldValue(
                  `prizes[${props.prizeIndex}].ss`,
                  isWeek ? StockStrategy.None : StockStrategy.Week
                );
                if (isWeek) {
                  props.setFieldValue(`prizes[${props.prizeIndex}].iQty`, 0);
                }
              }}
            />
            <KC_UISmall
              className="h-100 d-flex align-items-center"
              weight={KC_Weights.reg}
              text={tt("jp_weekly_quantity_limit_label")}
            />
          </div>
          {props.config.prizes[props.prizeIndex].ss !== StockStrategy.None &&
            renderQuantityField()}
        </div>
      </div>
      {props.hasAdvancedCouponOptions && (
        <>
          <ExpiryDelaySelector
            idx={props.prizeIndex}
            setFieldValue={props.setFieldValue}
            value={props.config?.prizes[props.prizeIndex]?.expiry?.value ?? 0}
          />
          {renderUnBlurDelay()}
          {renderPrizeCondition()}
        </>
      )}
    </div>
  );
}
